import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { usePeriod } from '../lib/period';
import { Badge, Card, EmptyState, ErrorState, LoadingBlock, PageHeader, Pagination, ProgressBar, type BadgeTone } from '../components/ui';
import { AiPanel, Section, useNumber } from '../components/kit';
import { Fact, hrefWith, periodQueryOf } from '../components/a-common';
import { AiActions, AiStatusBadge, ReportRefLine, type InsightItem, type InsightsList } from '../components/a-ai';
import { evidenceHref } from '../components/a-overview';

/**
 * AI Insights (CR-008 §12.3–§12.4): AI's briefings on the stored reports — always labelled as AI's, each
 * citing the figures it read, and never a source of figures. Whether AI is on, and this month's usage
 * against its budget; a briefing can be asked again only while AI is enabled. Off, every page still works.
 */

const STATE_TONE: Record<string, BadgeTone> = { ENABLED: 'success', DISABLED: 'neutral', NOT_CONFIGURED: 'warning', BUDGET_EXCEEDED: 'warning' };

/** Settings › Executive Dashboard's dated price estimate — only its currency and day, to label the estimated cost. */
interface PriceEstimate {
  currency: string | null;
  asOf: string | null;
}

export function InsightsPage() {
  const { t, has } = useI18n();
  const { param, setParam, query } = usePeriod();
  const num = useNumber();
  const queryClient = useQueryClient();
  const page = Math.max(1, Number(param('page')) || 1);

  const list = useQuery({
    queryKey: ['executive', 'insights', page],
    queryFn: () => api.get<InsightsList>('/executive/insights', { page }),
  });
  const costShown = list.data?.availability.estimatedCost !== null && list.data?.availability.estimatedCost !== undefined;
  // The estimate's currency lives with its prices in the settings; asked only when there is a cost to label.
  const price = useQuery({
    queryKey: ['executive', 'settings', 'ai-price-estimate'],
    queryFn: async (): Promise<PriceEstimate> => {
      const s = await api.get<{ ai?: { priceEstimate?: { currency?: string | null; asOf?: string | null } } }>('/executive/settings');
      return { currency: s?.ai?.priceEstimate?.currency ?? null, asOf: s?.ai?.priceEstimate?.asOf ?? null };
    },
    enabled: costShown,
    staleTime: 5 * 60_000,
  });

  // Saved first: each briefing offers one explicit action — Regenerate (confirmed), or Try again after a failure.
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['executive', 'insights'] });

  const availability = list.data?.availability;
  const state = availability?.state ?? 'DISABLED';
  const enabled = state === 'ENABLED';
  const stateKey = `insights.state.${state}`;
  const stateHintKey = `insights.stateHint.${state}`;

  const costText = (cost: number) => {
    const digits = cost < 1 ? 4 : 2;
    const currency = price.data?.currency;
    const amount = num(cost, digits);
    return currency ? `${currency} ${amount}` : amount;
  };

  return (
    <>
      <PageHeader title={t('insights.title')} subtitle={t('insights.subtitle')} count={list.data?.total} />

      {list.isLoading ? (
        <LoadingBlock />
      ) : list.error || !list.data || !availability ? (
        <Card>
          <ErrorState error={list.error ?? t('common.error')} onRetry={() => void list.refetch()} />
        </Card>
      ) : (
        <div className="space-y-6">
          <Section title={t('insights.availability')}>
            <div className="flex flex-wrap items-center gap-3">
              <Badge tone={STATE_TONE[state] ?? 'neutral'} dot>
                {has(stateKey) ? t(stateKey) : state}
              </Badge>
              {has(stateHintKey) && <span className="text-body-sm text-muted">{t(stateHintKey)}</span>}
            </div>
            <dl className="mt-5 grid gap-5 sm:grid-cols-3">
              <Fact label={t('insights.usage')}>
                <span className="tabular">{t('insights.tokens', { used: num(availability.used), budget: num(availability.budget) })}</span>
                <span className="mt-1.5 block">
                  <ProgressBar percent={availability.budget > 0 ? (100 * availability.used) / availability.budget : 0} tone={availability.used >= availability.budget ? 'error' : 'brand'} />
                </span>
              </Fact>
              <Fact label={t('insights.cost')}>
                {costShown && availability.estimatedCost !== null ? (
                  <>
                    <span className="tabular" dir="ltr">
                      {costText(availability.estimatedCost)}
                    </span>
                    <span className="mt-0.5 block text-caption text-muted">{price.data?.asOf ? t('insights.costEstimateAsOf', { date: price.data.asOf }) : t('insights.costEstimate')}</span>
                  </>
                ) : (
                  <span className="text-muted">{t('insights.noCost')}</span>
                )}
              </Fact>
              <Fact label={t('insights.model')}>
                <span className="font-mono text-mono-sm" dir="ltr">
                  {availability.model ?? '—'}
                </span>
              </Fact>
            </dl>
          </Section>

          <section aria-labelledby="briefings-title" className="space-y-4">
            <h2 id="briefings-title" className="text-h2 text-[var(--text-primary)]">
              {t('insights.list')}
            </h2>
            {list.data.items.length === 0 ? (
              <Card>
                <EmptyState icon={<Sparkles className="h-6 w-6" aria-hidden />} title={t('insights.none')} hint={enabled ? t('insights.noneHintEnabled') : t('insights.noneHint')} />
              </Card>
            ) : (
              list.data.items.map((item) => (
                <Briefing
                  key={item.id ?? `${item.report?.id}-${item.revision}`}
                  item={item}
                  query={query}
                  onChanged={refresh}
                  costText={costText}
                />
              ))
            )}
            {list.data.pageCount > 1 && (
              <Card padded={false}>
                <Pagination page={list.data.page} pageCount={list.data.pageCount} total={list.data.total} onChange={(p) => setParam('page', String(p))} />
              </Card>
            )}
          </section>
        </div>
      )}
    </>
  );
}

function Briefing({
  item,
  query,
  onChanged,
  costText,
}: {
  item: InsightItem;
  query: string;
  onChanged: () => void;
  costText: (cost: number) => string;
}) {
  const { t, has } = useI18n();
  const num = useNumber();
  const report = item.report;
  const reportQuery = report ? periodQueryOf(report.kind, report.periodStart, report.periodEnd) : query;
  const titleKey = `insights.kindTitle.${report?.kind ?? ''}`;
  const usage = item.usage;
  return (
    <article className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        {report ? <ReportRefLine report={report} /> : <span />}
        <span className="inline-flex flex-wrap items-center gap-2 text-caption text-muted">
          <AiStatusBadge status={item.status} />
          {item.revision !== undefined && <span>{t('insights.revision', { n: item.revision })}</span>}
        </span>
      </div>
      <AiPanel
        ai={item}
        title={has(titleKey) ? t(titleKey) : t('insights.briefing')}
        evidenceLink={(id) => evidenceHref(id, reportQuery)}
        action={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {report && (
              <Link to={hrefWith(`/reports/${report.id}`, query)} className="text-body-sm text-primary-600 hover:underline dark:text-primary-400">
                {t('insights.openReport')}
              </Link>
            )}
            {report && <AiActions ai={item} askPath={`/executive/reports/${report.id}/explanation`} onChanged={onChanged} />}
          </div>
        }
      />
      {(usage || item.failureCode) && (
        <p className="flex flex-wrap gap-x-3 px-1 text-caption text-[var(--text-tertiary)]">
          {usage && usage.inputTokens + usage.outputTokens > 0 && <span>{t('insights.itemTokens', { input: num(usage.inputTokens), output: num(usage.outputTokens) })}</span>}
          {usage && usage.estimatedCost !== null && <span>{t('insights.itemCost', { cost: costText(usage.estimatedCost) })}</span>}
          {item.failureCode && (
            <span>
              {t('insights.failure')}{' '}
              <span className="font-mono" dir="ltr">
                {item.failureCode}
              </span>
            </span>
          )}
        </p>
      )}
    </article>
  );
}
